import React, { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileText, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface AllenCountyTextParserProps {
  onImportComplete?: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  failedRecords: Array<{ record: any; error: string }>;
}

export const AllenCountyTextParser = ({ onImportComplete }: AllenCountyTextParserProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pastedText, setPastedText] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const parseDate = (dateStr: string): string => {
    try {
      // Parse formats like "9/23/2025 3:06:58 PM" or "9/23/2025"
      const match = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (match) {
        const [_, month, day, year] = match;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const extractConsideration = (line: string): number | null => {
    // Extract consideration amount like "Consideration: $60,000.00"
    const match = line.match(/Consideration:\s*\$([0-9,]+(?:\.[0-9]{2})?)/i);
    if (match) {
      return parseFloat(match[1].replace(/,/g, ''));
    }
    return null;
  };

  const analyzeOwnerFinance = (grantee: string, grantor: string): { isOwnerFinance: boolean; score: number; reasons: string[] } => {
    const bankKeywords = ['BANK', 'FEDERAL', 'MORTGAGE', 'LENDING', 'LOAN', 'FINANCIAL', 'TRUST CO', 'SAVINGS', 'CREDIT UNION', 'FSB', 'N.A.'];
    const corporateIndicators = ['LLC', 'INC', 'CORP', 'COMPANY', 'ENTERPRISES', 'INVESTMENTS', 'PROPERTIES'];
    
    let score = 50;
    const reasons: string[] = [];
    
    const granteeUpper = grantee.toUpperCase();
    const grantorUpper = grantor.toUpperCase();
    
    // Check if grantee is a bank (reduces score)
    if (bankKeywords.some(keyword => granteeUpper.includes(keyword))) {
      score -= 40;
      reasons.push('Grantee appears to be a financial institution');
    }
    
    // Check if grantor is a bank (increases score - bank selling to individual)
    if (bankKeywords.some(keyword => grantorUpper.includes(keyword))) {
      score += 20;
      reasons.push('Grantor is a financial institution (bank selling)');
    }
    
    // Family transfers (same last name) - moderate score
    const granteeNames = granteeUpper.split(/[,\/]/).map(n => n.trim());
    const grantorNames = grantorUpper.split(/[,\/]/).map(n => n.trim());
    
    let hasSameLastName = false;
    for (const granteeName of granteeNames) {
      const granteeWords = granteeName.split(/\s+/);
      const granteeLastWord = granteeWords[granteeWords.length - 1];
      
      for (const grantorName of grantorNames) {
        const grantorWords = grantorName.split(/\s+/);
        const grantorLastWord = grantorWords[grantorWords.length - 1];
        
        if (granteeLastWord && grantorLastWord && 
            granteeLastWord.length > 2 && 
            granteeLastWord === grantorLastWord) {
          hasSameLastName = true;
          break;
        }
      }
    }
    
    if (hasSameLastName) {
      score -= 10;
      reasons.push('Possible family transfer (same last name)');
    }
    
    // Corporate entities
    if (corporateIndicators.some(indicator => grantorUpper.includes(indicator))) {
      score += 15;
      reasons.push('Grantor is a corporate entity');
    }
    
    if (corporateIndicators.some(indicator => granteeUpper.includes(indicator))) {
      score += 10;
      reasons.push('Grantee is a corporate entity');
    }
    
    // Individual to individual increases score
    const hasCommaOrSlash = (name: string) => name.includes(',') || name.includes('/');
    if (!hasCommaOrSlash(granteeUpper) && !hasCommaOrSlash(grantorUpper)) {
      score += 10;
      reasons.push('Individual to individual transfer');
    }
    
    return {
      isOwnerFinance: score >= 50,
      score: Math.max(0, Math.min(100, score)),
      reasons
    };
  };

  const parseAllenCountyText = (text: string): any[] => {
    const records: any[] = [];
    
    // Split by separator lines - handle various line ending formats
    const sections = text.split(/[\r\n]+-{30,}[\r\n]+/).filter(s => s.trim());
    
    console.log(`Allen County Parser: Found ${sections.length} sections to process`);
    
    for (const section of sections) {
      try {
        const lines = section.split('\n').map(l => l.trim()).filter(l => l);
        
        const record: any = {
          county: 'allen',
          document_type: 'Land Contract',
          is_active: true,
          pdf_downloaded: false,
          pdf_parsed: false,
          ocr_attempted: false,
          address_needs_review: true,
          status: 'new'
        };
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          // Instrument number
          if (line.startsWith('Instrument:')) {
            const match = line.match(/Instrument:\s*(\S+)/);
            if (match) {
              record.document_number = match[1];
            }
          }
          
          // Recorded date
          if (line.startsWith('Recorded:')) {
            const match = line.match(/Recorded:\s*([0-9\/]+\s+[0-9:]+\s+[AP]M)/);
            if (match) {
              record.filing_date = parseDate(match[1]);
            }
          }
          
          // Document type and consideration - handle "Land ContractConsideration:" format
          if (line.includes('Document Type:')) {
            // Extract document type - stop at "Consideration" (with or without space) or "Pages"
            const docTypeMatch = line.match(/Document Type:\s*(.+?)(?:Consideration:|Pages:|$)/);
            if (docTypeMatch) {
              const docType = docTypeMatch[1].trim();
              if (docType) {
                record.document_type = docType;
              }
            }
            
            const considerationAmount = extractConsideration(line);
            if (considerationAmount) {
              record.loan_amount = considerationAmount;
            }
            
            // Pages
            const pagesMatch = line.match(/Pages:\s*(\d+)/);
            if (pagesMatch) {
              record.number_of_pages = parseInt(pagesMatch[1]);
            }
          }
          
          // Remarks
          if (line.startsWith('Remarks:')) {
            const remarks = line.replace('Remarks:', '').trim();
            if (remarks && remarks !== '') {
              record.notes = remarks;
            }
          }
          
          // Vendor (Grantor/Seller)
          if (line.startsWith('Vendor:')) {
            record.grantor_name = line.replace('Vendor:', '').trim();
          }
          
          // Vendee (Grantee/Buyer)
          if (line.startsWith('Vendee:')) {
            record.grantee_name = line.replace('Vendee:', '').trim();
          }
          
          // Legal Description
          if (line.startsWith('Legal Description:')) {
            let legalDesc = line.replace('Legal Description:', '').trim();
            
            // Continue reading subsequent lines that are part of legal description
            while (i + 1 < lines.length && !lines[i + 1].match(/^(Marginal|Disposition|Instrument):/)) {
              i++;
              legalDesc += ' ' + lines[i].trim();
            }
            
            record.legal_description = legalDesc;
            
            // Extract parcel number
            const parcelMatch = legalDesc.match(/Parcel:\s*(\S+)/);
            if (parcelMatch) {
              record.raw_data = { parcel_number: parcelMatch[1] };
            }
            
            // Try to extract property location from legal description
            const locationMatch = legalDesc.match(/^\s*([A-Z\s]+?)(?:\s+Lot:|Sec:|PT)/);
            if (locationMatch) {
              record.property_address = locationMatch[1].trim() + ' Township';
            }
          }
          
          // Marginal references (related documents)
          if (line.startsWith('Marginal:')) {
            const marginal = line.replace('Marginal:', '').trim();
            if (record.notes) {
              record.notes += ' | Marginal: ' + marginal;
            } else {
              record.notes = 'Marginal: ' + marginal;
            }
          }
        }
        
        // Analyze for owner finance if we have grantor and grantee
        if (record.grantor_name && record.grantee_name) {
          const analysis = analyzeOwnerFinance(record.grantee_name, record.grantor_name);
          record.is_potential_owner_finance = analysis.isOwnerFinance;
          record.confidence_score = analysis.score;
          record.detection_reasons = analysis.reasons;
        }
        
        // Only add if we have minimum required fields
        if (record.document_number && record.filing_date) {
          record.search_date = new Date().toISOString();
          records.push(record);
          console.log(`Parsed record: ${record.document_number} - ${record.grantor_name} -> ${record.grantee_name}`);
        } else {
          console.log('Skipped section - missing required fields:', { 
            has_doc_number: !!record.document_number, 
            has_filing_date: !!record.filing_date 
          });
        }
      } catch (error) {
        console.error('Error parsing section:', error);
      }
    }
    
    console.log(`Allen County Parser: Successfully parsed ${records.length} records from ${sections.length} sections`);
    return records;
  };

  const processText = async (text: string) => {
    if (!text.trim()) {
      toast({
        title: "No text to process",
        description: "Please paste or upload text content",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      console.log('Starting Allen County text parsing...');
      const records = parseAllenCountyText(text);
      console.log(`Parsing complete. Found ${records.length} records`);
      
      if (records.length === 0) {
        toast({
          title: "No records found",
          description: "Could not parse any land contract records from the text. Check console for details.",
          variant: "destructive"
        });
        setIsProcessing(false);
        return;
      }

      toast({
        title: "Processing records",
        description: `Parsed ${records.length} records, importing to database...`
      });

      const result: ImportResult = {
        success: 0,
        failed: 0,
        failedRecords: []
      };

      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        setProgress(((i + 1) / records.length) * 100);

        try {
          // Check for duplicates
          const { data: existing } = await supabase
            .from('county_research')
            .select('id')
            .eq('document_number', record.document_number)
            .eq('county', 'allen')
            .maybeSingle();

          if (existing) {
            console.log(`Skipping duplicate: ${record.document_number}`);
            result.failed++;
            result.failedRecords.push({
              record,
              error: 'Duplicate record'
            });
            continue;
          }

          const { error } = await supabase
            .from('county_research')
            .insert(record);

          if (error) {
            result.failed++;
            result.failedRecords.push({
              record,
              error: error.message
            });
          } else {
            result.success++;
          }
        } catch (error: any) {
          result.failed++;
          result.failedRecords.push({
            record,
            error: error.message
          });
        }
      }

      setImportResult(result);
      
      toast({
        title: "Import complete",
        description: `Successfully imported ${result.success} records. ${result.failed} failed.`,
        variant: result.failed > 0 ? "destructive" : "default"
      });

      if (result.success > 0 && onImportComplete) {
        onImportComplete();
      }

      setPastedText('');
    } catch (error: any) {
      console.error('Error processing text:', error);
      toast({
        title: "Processing error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      await processText(text);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast({
        title: "Error reading file",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handlePasteImport = () => {
    processText(pastedText);
  };

  return (
    <div className="space-y-6">
      {importResult && (
        <Alert variant={importResult.failed > 0 ? "destructive" : "default"}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">
                Import Results: {importResult.success} successful, {importResult.failed} failed
              </p>
              {importResult.failedRecords.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer hover:underline">
                    View failed records ({importResult.failedRecords.length})
                  </summary>
                  <ul className="mt-2 space-y-1 ml-4">
                    {importResult.failedRecords.slice(0, 10).map((item, idx) => (
                      <li key={idx}>
                        Doc #{item.record.document_number}: {item.error}
                      </li>
                    ))}
                    {importResult.failedRecords.length > 10 && (
                      <li className="text-muted-foreground">
                        ... and {importResult.failedRecords.length - 10} more
                      </li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Allen County Text Import
          </CardTitle>
          <CardDescription>
            Import land contract records from Allen County, OH text files
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Option 1: Upload Text File</h4>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt"
              onChange={handleFileUpload}
              className="hidden"
              id="allen-file-upload"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
              className="w-full"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Text File
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">Option 2: Paste Text</h4>
            <Textarea
              placeholder="Paste Allen County land contract records here..."
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              disabled={isProcessing}
              className="min-h-[200px] font-mono text-sm"
            />
            <Button
              onClick={handlePasteImport}
              disabled={isProcessing || !pastedText.trim()}
              className="w-full mt-2"
            >
              <FileText className="mr-2 h-4 w-4" />
              Import Pasted Text
            </Button>
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                Processing... {Math.round(progress)}%
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <p className="font-semibold mb-2">Expected Format:</p>
          <ul className="text-sm space-y-1 ml-4 list-disc">
            <li>Records separated by dashed lines (------)</li>
            <li>Instrument number (document number)</li>
            <li>Recorded date (filing date)</li>
            <li>Vendor (grantor/seller) and Vendee (grantee/buyer)</li>
            <li>Legal description with parcel information</li>
            <li>Consideration amount (if available)</li>
          </ul>
          <p className="text-sm mt-2 text-muted-foreground">
            The parser will automatically analyze records for potential owner-finance deals.
          </p>
        </AlertDescription>
      </Alert>
    </div>
  );
};
